import './InfoCard.scss';
import { memo, PropsWithChildren, ReactNode } from 'react';

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icons, Tooltip } from 'plume-ui';

import { Routes } from '../../../../config';
import { useCreateRoute } from '../../../../utils/hooks/useRedirectToRoute';

export type InfoCardGridItem = {
  label: string;
  value: ReactNode;
  tooltip?: string;
};

export type InfoCardProps = PropsWithChildren<{
  label: ReactNode;
  labelTooltip?: string;
  optionalTitle?: ReactNode;
  showEdit?: boolean;
  editRoute?: { route: Routes; params?: { [key: string]: string | undefined } };
  headerRightContent?: ReactNode;
  gridData?: InfoCardGridItem[];
  noContentOverlay?: ReactNode;
}>;

const InfoCard = ({
  children,
  label,
  showEdit,
  labelTooltip,
  optionalTitle,
  editRoute,
  headerRightContent,
  gridData,
  noContentOverlay,
}: InfoCardProps) => {
  const { t } = useTranslation();
  const routeToRedirect = useCreateRoute();

  return (
    <div className="InfoCard">
      <div className="InfoCard__header">
        <div className="InfoCard__headerLeft">
          <div className="InfoCard__title">{label}</div>
          {optionalTitle && (
            <span className="InfoCard__title--optional">{optionalTitle}</span>
          )}
          {labelTooltip && (
            <Tooltip
              label={labelTooltip}
              overrideMaxCharacters
              liveMeasure={false}
            >
              <Icons.InfoIcon width={16} />
            </Tooltip>
          )}
        </div>
        <div className="InfoCard__headerRight">{headerRightContent}</div>
      </div>
      {showEdit && (
        <div className="InfoCard__edit">
          <Link
            to={{
              pathname: routeToRedirect(
                editRoute ? editRoute.route : Routes.HealthSummary,
                editRoute?.params,
              ),
              state: editRoute?.params,
            }}
          >
            {t('actions.edit')}
          </Link>
        </div>
      )}
      {gridData && (
        <div className="InfoGrid">
          {gridData.map((data) => (
            <>
              <div className="InfoGrid__title">
                {data.label}
                {data.tooltip && (
                  <Tooltip label={data.tooltip}>
                    <Icons.InfoIcon width={16} />
                  </Tooltip>
                )}
              </div>
              <div className="InfoGrid__value">{data.value}</div>
            </>
          ))}
        </div>
      )}
      {children}
      {!gridData && !children && (
        <div className="InfoCard__noContent">{noContentOverlay}</div>
      )}
    </div>
  );
};

export default memo(InfoCard);
